/**
 * The two parts of a plain-text email body. Both strings are exact slices of
 * the input, so joining them always reproduces the original body byte-for-byte.
 */
export type EmailBodyParts = {
  currentText: string;
  quotedText: string;
  hasQuotedText: boolean;
};

type SenderIdentity = {
  fromEmail: string;
  fromName?: string | null;
};

type ApplicationIdentity = {
  applicantEmail: string;
  applicantName: string;
};

type ContactIdentity = {
  email: string;
  firstName: string;
  lastName: string;
};

export type EmailIdentitySources = {
  applications?: readonly ApplicationIdentity[];
  contacts?: readonly ContactIdentity[];
};

type BodyLine = {
  start: number;
  text: string;
};

const GMAIL_DATE_HINT = /(?:@|\b(?:mon|tue|wed|thu|fri|sat|sun)(?:day)?\b|\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)(?:uary|ruary|ch|il|e|y|ust|tember|ober|ember)?\b|\b\d{1,2}[/-]\d{1,2}(?:[/-]\d{2,4})?\b)/i;
const OUTLOOK_HEADER = /^(from|sent|to|subject):\s*\S/i;
const GENERIC_LOCAL_PARTS = new Set([
  'admin',
  'contact',
  'hello',
  'info',
  'mail',
  'noreply',
  'no-reply',
  'office',
  'sales',
  'support',
  'team',
]);

function bodyLines(body: string): BodyLine[] {
  const lines: BodyLine[] = [];
  let start = 0;

  while (start <= body.length) {
    const newline = body.indexOf('\n', start);
    const end = newline === -1 ? body.length : newline;
    const raw = body.slice(start, end);
    lines.push({ start, text: raw.endsWith('\r') ? raw.slice(0, -1) : raw });
    if (newline === -1) break;
    start = newline + 1;
  }

  return lines;
}

function looksLikeGmailAttribution(value: string): boolean {
  const compact = value.replace(/\s+/g, ' ').trim();
  return /^on\s+.+\bwrote:\s*$/i.test(compact) && GMAIL_DATE_HINT.test(compact);
}

function gmailAttributionIndex(lines: BodyLine[]): number | null {
  for (let index = 0; index < lines.length; index += 1) {
    if (!/^\s*on\s+/i.test(lines[index].text)) continue;

    // Mail clients occasionally wrap the attribution before the address or
    // "wrote:". Joining only a few lines keeps detection conservative.
    let candidate = '';
    for (let offset = 0; offset < 4 && index + offset < lines.length; offset += 1) {
      candidate += `${offset ? ' ' : ''}${lines[index + offset].text.trim()}`;
      if (looksLikeGmailAttribution(candidate)) return lines[index].start;
      if (/\bwrote:\s*$/i.test(candidate)) break;
    }
  }

  return null;
}

function originalMessageIndex(lines: BodyLine[]): number | null {
  const marker = /^\s*-{2,}\s*(?:original message|forwarded message)\s*-{2,}\s*$/i;
  return lines.find((line) => marker.test(line.text))?.start ?? null;
}

function outlookHeaderIndex(lines: BodyLine[]): number | null {
  for (let index = 0; index < lines.length; index += 1) {
    if (!/^\s*from:\s*\S/i.test(lines[index].text)) continue;

    const headers = new Set<string>();
    for (let offset = 0; offset < 8 && index + offset < lines.length; offset += 1) {
      const text = lines[index + offset].text.trim();
      if (!text) continue;
      const match = text.match(OUTLOOK_HEADER);
      if (!match) break;
      headers.add(match[1].toLowerCase());
    }

    if (headers.has('from') && headers.has('sent') && (headers.has('to') || headers.has('subject'))) {
      return lines[index].start;
    }
  }

  return null;
}

function quotedBlockIndex(lines: BodyLine[]): number | null {
  for (let index = 0; index < lines.length; index += 1) {
    if (!/^\s*>/.test(lines[index].text)) continue;

    let quotedLines = 0;
    let isTrailingQuote = true;
    for (let offset = index; offset < lines.length; offset += 1) {
      const text = lines[offset].text;
      if (!text.trim()) continue;
      if (!/^\s*>/.test(text)) {
        isTrailingQuote = false;
        break;
      }
      quotedLines += 1;
    }

    // Only a trailing run is treated as history. A greater-than comparison in
    // the middle of the current message therefore remains visible.
    if (isTrailingQuote && quotedLines >= 1) return lines[index].start;
  }

  return null;
}

/**
 * Separates the author's current plain-text reply from common quoted-history
 * formats emitted by Gmail, Outlook, and other mail clients.
 */
export function splitEmailBody(body: string): EmailBodyParts {
  if (!body) return { currentText: body, quotedText: '', hasQuotedText: false };

  const lines = bodyLines(body);
  const candidates = [
    gmailAttributionIndex(lines),
    originalMessageIndex(lines),
    outlookHeaderIndex(lines),
    quotedBlockIndex(lines),
  ].filter((index): index is number => index !== null);

  if (!candidates.length) return { currentText: body, quotedText: '', hasQuotedText: false };

  const quotedStart = Math.min(...candidates);
  return {
    currentText: body.slice(0, quotedStart),
    quotedText: body.slice(quotedStart),
    hasQuotedText: true,
  };
}

/** Lower-case address normalization used only for identity matching. */
export function normalizeEmailAddress(value: string): string {
  const trimmed = value.trim();
  const bracketed = trimmed.match(/<\s*([^<>]+@[^<>]+)\s*>/);
  return (bracketed?.[1] ?? trimmed).replace(/^mailto:/i, '').trim().toLocaleLowerCase('en-US');
}

function cleanName(value: string | null | undefined): string | null {
  const clean = value?.replace(/\s+/g, ' ').trim();
  return clean || null;
}

function readableLocalPart(email: string): string | null {
  const normalized = normalizeEmailAddress(email);
  const at = normalized.lastIndexOf('@');
  if (at <= 0) return null;

  const local = normalized.slice(0, at);
  if (GENERIC_LOCAL_PARTS.has(local) || local.length > 40 || !/^[\p{L}._-]+$/u.test(local)) return null;

  const words = local.split(/[._-]+/).filter(Boolean);
  if (words.length < 2 || words.some((word) => word.length < 2 || GENERIC_LOCAL_PARTS.has(word))) return null;

  return words.map((word) => `${word.charAt(0).toLocaleUpperCase('en-US')}${word.slice(1)}`).join(' ');
}

/**
 * Returns the most trustworthy sender label available. Stored sender metadata
 * wins, followed by known CMS records, then a literal prettification of an
 * obviously readable local part. Opaque addresses remain addresses.
 */
export function resolveEmailDisplayName(sender: SenderIdentity, sources: EmailIdentitySources = {}): string {
  const address = normalizeEmailAddress(sender.fromEmail);
  const fromName = cleanName(sender.fromName);
  if (fromName && normalizeEmailAddress(fromName) !== address) return fromName;

  const application = sources.applications?.find((record) => normalizeEmailAddress(record.applicantEmail) === address);
  const applicationName = cleanName(application?.applicantName);
  if (applicationName) return applicationName;

  const contact = sources.contacts?.find((record) => normalizeEmailAddress(record.email) === address);
  const contactName = cleanName(contact ? `${contact.firstName} ${contact.lastName}` : null);
  if (contactName) return contactName;

  return readableLocalPart(address) ?? (sender.fromEmail.trim() || address);
}
